import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { SearchInput } from '@/components/ui/search-input';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import { useDebounce } from '@/hooks/useDebounce';
import { usePublicSearch } from '@/hooks/usePublicPortal';
import { PUBLIC_ROUTES } from '@/constants';

const SECTIONS = [
  { key: 'tournaments', label: 'Tournaments', route: (item) => PUBLIC_ROUTES.TOURNAMENT.replace(':id', item._id) },
  { key: 'teams', label: 'Teams', route: (item) => PUBLIC_ROUTES.TEAM.replace(':id', item._id) },
  { key: 'players', label: 'Players', route: (item) => PUBLIC_ROUTES.PLAYER.replace(':id', item._id) },
  { key: 'grounds', label: 'Grounds', route: (item) => PUBLIC_ROUTES.GROUND.replace(':id', item._id) },
  { key: 'matches', label: 'Matches', route: (item) => PUBLIC_ROUTES.MATCH.replace(':matchId', item._id) },
  { key: 'sponsors', label: 'Sponsors', route: () => PUBLIC_ROUTES.SPONSORS },
  { key: 'gallery', label: 'Gallery', route: () => PUBLIC_ROUTES.GALLERY },
];

function getItemLabel(item, sectionKey) {
  if (sectionKey === 'players') return item.fullName || item.nickName;
  if (sectionKey === 'matches') {
    const a = item.teamA?.name || 'Team A';
    const b = item.teamB?.name || 'Team B';
    return `${a} vs ${b}`;
  }
  return item.name || item.title || 'Untitled';
}

function getItemMeta(item, sectionKey) {
  if (sectionKey === 'players') return item.team?.name || item.position;
  if (sectionKey === 'matches') return item.tournament?.name || item.court;
  if (sectionKey === 'tournaments') return item.venue || item.status;
  if (sectionKey === 'grounds') return item.city;
  if (sectionKey === 'sponsors') return item.category;
  if (sectionKey === 'gallery') return item.caption || item.type;
  return item.shortName || item.district;
}

function PublicSearchDialog({ open, onOpenChange }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebounce(query, 300);
  const { data, isFetching, isFetched } = usePublicSearch(debouncedQuery, { enabled: open });

  const hasResults = useMemo(
    () => SECTIONS.some((section) => (data?.[section.key] || []).length > 0),
    [data]
  );

  const handleSelect = (href) => {
    onOpenChange(false);
    setQuery('');
    navigate(href);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-hidden sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Search VolleyPro</DialogTitle>
          <DialogDescription>
            Find tournaments, teams, players, grounds, matches, and more.
          </DialogDescription>
        </DialogHeader>

        <SearchInput
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onClear={() => setQuery('')}
          placeholder="Search tournaments, teams, players..."
          autoFocus
        />

        <div className="max-h-[50vh] overflow-y-auto pr-1">
          {debouncedQuery.length < 2 && (
            <p className="py-8 text-center text-sm text-muted-foreground">
              Type at least 2 characters to search.
            </p>
          )}

          {debouncedQuery.length >= 2 && isFetching && !data && (
            <div className="space-y-3 py-4">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          )}

          {debouncedQuery.length >= 2 && isFetched && !hasResults && (
            <EmptyState
              icon={Search}
              title="No results found"
              description={`Nothing matched "${debouncedQuery}". Try another search term.`}
              className="py-10"
            />
          )}

          {hasResults &&
            SECTIONS.map((section) => {
              const items = data?.[section.key] || [];
              if (!items.length) return null;

              return (
                <section key={section.key} className="mb-6 last:mb-0">
                  <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {section.label}
                  </h3>
                  <ul className="divide-y divide-border rounded-lg border border-border">
                    {items.map((item) => (
                      <li key={item._id || `${section.key}-${getItemLabel(item, section.key)}`}>
                        <button
                          type="button"
                          className="flex w-full flex-col items-start gap-0.5 px-4 py-3 text-left transition-colors hover:bg-muted/60"
                          onClick={() => handleSelect(section.route(item))}
                        >
                          <span className="font-medium text-foreground">
                            {getItemLabel(item, section.key)}
                          </span>
                          {getItemMeta(item, section.key) && (
                            <span className="text-xs text-muted-foreground">
                              {getItemMeta(item, section.key)}
                            </span>
                          )}
                        </button>
                      </li>
                    ))}
                  </ul>
                </section>
              );
            })}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default PublicSearchDialog;
